"use client"

import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { Code2, Copy, ExternalLink } from "lucide-react"
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import useAnalytics from '../analytics/analytics';

interface CopyCodeModalProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    agId: string;
    onCopy?: (platform: string) => void;
}

interface CopyCodeComponentProps {
    agId: string;
    onCopy: (platform: string) => void;
}

// Move these outside both components
const getStaticEmbedCode = (agId: string) => `<script>
(function() {
    function initAgent() {
        window.aimdoc.initAgentChat('ai-assistant', {
            agentId: '${agId}'
        });
    }
    var script = document.createElement('script');
    script.src = '${import.meta.env.VITE_APP_URL}/embedded.bundle.js';
    script.async = true;
    script.onload = initAgent;
    document.body.appendChild(script);
})();
</script>`;

const getNextEmbedCode = (agId: string) => `import Script from "next/script";

export default function AimDocWidget() {
  return (
    <>
      <Script
        src="https://app.aimdoc.ai/embedded.bundle.js"
        strategy="afterInteractive"
      />
      <Script strategy="afterInteractive">
        {\`
          window.addEventListener('load', function() {
            if (window.aimdoc) {
              window.aimdoc.initAgentChat('ai-assistant', {
                agentId: '${agId}'
              });
            }
          });
        \`}
      </Script>
    </>
  );
}`;

const getReactEmbedCode = (agId: string) => `import { AimdocProvider } from '@aimdoc/sdk-react';

export default function App() {
  return (
    <AimdocProvider agentId="${agId}">
      {/* Your app content */}
    </AimdocProvider>
  );
}`

const TAB_CONTENTS = [
    {
        value: "static",
        title: "For Framer, Wix, Webflow, or static HTML",
        description: "Add before closing </body> tag",
        getCode: (agId: string) => getStaticEmbedCode(agId),
    },
    {
        value: "nextjs",
        title: "Add to your layout.tsx file",
        description: "Works with Next.js 13+ App Router",
        getCode: (agId: string) => getNextEmbedCode(agId),
    },
    {
        value: "react",
        title: "Add as a component in your React app",
        description: "Compatible with React 16.8+",
        getCode: (agId: string) => getReactEmbedCode(agId),
    }
];

export function CopyCodeComponent({ agId, onCopy }: CopyCodeComponentProps) {
    const [, setActiveTab] = useState("static")
    
    const handleCopy = async (platform: string) => {
        let textToCopy;
        switch (platform) {
            case "static":
                textToCopy = getStaticEmbedCode(agId);
                break;
            case "nextjs":
                textToCopy = getNextEmbedCode(agId);
                break;
            case "react":
                textToCopy = getReactEmbedCode(agId);
                break;
            default:
                textToCopy = getStaticEmbedCode(agId);
        }
        try {
            await navigator.clipboard.writeText(textToCopy);
            onCopy(platform);
        } catch (err) {
            toast.error('Failed to copy code', toastConfig);
        }
    };

    return (
        <>
            <Tabs defaultValue="static" className="w-full" onValueChange={setActiveTab}>
                <TabsList className="h-9">
                    <TabsTrigger value="static" className="flex items-center gap-1 text-xs">
                        <WebsiteJSIcon className="w-3 h-3" />
                        Static
                    </TabsTrigger>
                    <TabsTrigger value="nextjs" className="flex items-center gap-1 text-xs">
                        <NextjsIcon className="w-3 h-3" />
                        Next.js
                    </TabsTrigger>
                    <TabsTrigger value="react" className="flex items-center gap-1 text-xs">
                        <ReactIcon className="w-3 h-3" />
                        React
                    </TabsTrigger>
                </TabsList>

                <div className="mt-3">
                    {TAB_CONTENTS.map((tab) => (
                        <TabsContent key={tab.value} value={tab.value} className="space-y-3">
                            <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">{tab.title}</p>
                                <div className="relative">
                                    <div className="overflow-hidden rounded-lg border bg-muted/20">
                                        <div className="flex items-center justify-between border-b px-3 py-2">
                                            <span className="text-xs text-muted-foreground">{tab.value === 'static' ? 'index.html' : tab.value === 'nextjs' ? 'layout.tsx' : 'App.tsx'}</span>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleCopy(tab.value)}
                                                className="h-7 px-2 text-xs"
                                            >
                                                <Copy className="mr-1 h-3 w-3" />
                                                Copy
                                            </Button>
                                        </div>
                                        <pre className="overflow-x-auto bg-background/50 p-4">
                                            <code className="text-xs font-mono text-foreground/90">{tab.getCode(agId).trim()}</code>
                                        </pre>
                                    </div>
                                </div>
                                {tab.description && (
                                    <p className="text-xs text-muted-foreground">
                                        {tab.description}
                                    </p>
                                )}
                            </div>
                        </TabsContent>
                    ))}
                </div>
            </Tabs>
        </>
    );
}

const WebsiteJSIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M9.29289 1.29289C9.48043 1.10536 9.73478 1 10 1H18C19.6569 1 21 2.34315 21 4V9C21 9.55228 20.5523 10 20 10C19.4477 10 19 9.55228 19 9V4C19 3.44772 18.5523 3 18 3H11V8C11 8.55228 10.5523 9 10 9H5V20C5 20.5523 5.44772 21 6 21H7C7.55228 21 8 21.4477 8 22C8 22.5523 7.55228 23 7 23H6C4.34315 23 3 21.6569 3 20V8C3 7.73478 3.10536 7.48043 3.29289 7.29289L9.29289 1.29289ZM6.41421 7H9V4.41421L6.41421 7ZM16 14.75C16 13.2312 17.2312 12 18.75 12H21C21.5523 12 22 12.4477 22 13C22 13.5523 21.5523 14 21 14H18.75C18.3358 14 18 14.3358 18 14.75C18 15.1642 18.3358 15.5 18.75 15.5H19.25C20.7688 15.5 22 16.7312 22 18.25C22 19.7688 20.7688 21 19.25 21H17C16.4477 21 16 20.5523 16 20C16 19.4477 16.4477 19 17 19H19.25C19.6642 19 20 18.6642 20 18.25C20 17.8358 19.6642 17.5 19.25 17.5H18.75C17.2312 17.5 16 16.2688 16 14.75ZM15 13C15 12.4477 14.5523 12 14 12C13.4477 12 13 12.4477 13 13V18C13 18.5523 12.5523 19 12 19C11.44772 19 11 18.5523 11 18V17.6667C11 17.1144 10.5523 16.6667 10 16.6667C9.44772 16.6667 9 17.1144 9 17.6667V18C9 19.6569 10.3431 21 12 21C13.6569 21 15 19.6569 15 18V13Z" fill="currentColor"/>
  </svg>
);

const ReactIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M12,10.11A1.87,1.87,0,1,1,10.13,12,1.88,1.88,0,0,1,12,10.11M7.37,20c.63.38,2-.2,3.6-1.7a24.22,24.22,0,0,1-1.51-1.9A22.7,22.7,0,0,1,7.06,16c-.51,2.14-.32,3.61.31,4m.71-5.74-.29-.51a7.91,7.91,0,0,0-.29.86c.27.06.57.11.88.16l-.3-.51m6.54-.76.81-1.5-.81-1.5c-.3-.53-.62-1-.91-1.47C13.17,9,12.6,9,12,9s-1.17,0-1.71,0c-.29.47-.61.94-.91,1.47L8.57,12l.81,1.5c.3.53.62,1,.91,1.47.54,0,1.11,0,1.71,0s1.17,0,1.71,0c.29-.47.61-.94.91-1.47M12,6.78c-.19.22-.39.45-.59.72h1.18c-.2-.27-.4-.5-.59-.72m0,10.44c.19-.22.39-.45.59-.72H11.41c.2.27.4.5.59.72M16.62,4c-.62-.38-2,.2-3.59,1.7a24.22,24.22,0,0,1,1.51,1.9,22.7,22.7,0,0,1,2.4.36c.51-2.14.32-3.61-.32-4m-.7,5.74.29.51a7.91,7.91,0,0,0,.29-.86c-.27-.06-.57-.11-.88-.16l.3.51m1.45-7c1.47.84,1.63,3.05,1,5.63,2.54.75,4.37,2,4.37,3.68s-1.83,2.93-4.37,3.68c.62,2.58.46,4.79-1,5.63s-3.45-.12-5.37-1.95c-1.92,1.83-3.91,2.79-5.38,1.95s-1.62-3-1-5.63c-2.54-.75-4.37-2-4.37-3.68S3.08,9.07,5.62,8.32c-.62-2.58-.46-4.79,1-5.63s3.46.12,5.38,1.95c1.92-1.83,3.91-2.79,5.37-1.95M17.08,12A22.51,22.51,0,0,1,18,14.26c2.1-.63,3.28-1.53,3.28-2.26S20.07,10.37,18,9.74A22.51,22.51,0,0,1,17.08,12M6.92,12A22.51,22.51,0,0,1,6,9.74c-2.1.63-3.28,1.53-3.28,2.26S3.93,13.63,6,14.26A22.51,22.51,0,0,1,6.92,12m9,2.26-.3.51c.31,0,.61-.10.88-.16a7.91,7.91,0,0,0-.29-.86l-.29.51M13,18.3c1.59,1.5,3,2.08,3.59,1.7s.83-1.82.32-4a22.7,22.7,0,0,1-2.4.36A24.22,24.22,0,0,1,13,18.3M8.08,9.74l.3-.51c-.31,0-.61.10-.88.16a7.91,7.91,0,0,0,.29.86l.29-.51M11,5.7C9.38,4.2,8,3.62,7.37,4s-.82,1.82-.31,4a22.7,22.7,0,0,1,2.4-.36A24.22,24.22,0,0,1,11,5.7Z" />
  </svg>
);

const NextjsIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M119.616813,0.0688905149 C119.066276,0.118932037 117.314565,0.294077364 115.738025,0.419181169 C79.3775171,3.69690087 45.3192571,23.3131775 23.7481916,53.4631946 C11.7364614,70.2271045 4.05395894,89.2428829 1.15112414,109.384595 C0.12512219,116.415429 0,118.492153 0,128.025062 C0,137.557972 0.12512219,139.634696 1.15112414,146.665529 C8.10791789,194.730411 42.3163245,235.11392 88.7116325,250.076335 C97.0197458,252.753556 105.778299,254.580072 115.738025,255.680985 C119.616813,256.106338 136.383187,256.106338 140.261975,255.680985 C157.453763,253.779407 172.017986,249.525878 186.382014,242.194795 C188.584164,241.068861 189.00958,240.768612 188.709286,240.518404 C188.509091,240.36828 179.124927,227.782837 167.86393,212.570214 L147.393939,184.922273 L121.743891,146.965779 C107.630108,126.098464 96.0187683,109.034305 95.9186706,109.034305 C95.8185728,109.009284 95.7184751,125.873277 95.6684262,146.465363 C95.5933529,182.52028 95.5683284,183.971484 95.1178886,184.82219 C94.4672532,186.048207 93.9667644,186.548623 92.915738,187.099079 C92.114956,187.499411 91.4142717,187.574474 87.6355816,187.574474 L83.3063539,187.574474 L82.1552297,186.848872 C81.4044966,186.373477 80.8539589,185.747958 80.4785924,185.022356 L79.9530792,183.896422 L80.0031281,133.729796 L80.0782014,83.5381493 L80.8539589,82.5623397 C81.25435,82.0369037 82.1051808,81.3613431 82.7057674,81.0360732 C83.7317693,80.535658 84.1321603,80.4856165 88.4613881,80.4856165 C93.5663734,80.4856165 94.4172043,80.6857826 95.7434995,82.1369867 C96.1188661,82.5373189 110.007429,103.454675 126.623656,128.650581 C143.239883,153.846488 165.962072,188.250034 177.122972,205.139048 L197.392766,235.839522 L198.418768,235.163961 C207.502639,229.259062 217.112023,220.852086 224.719453,212.09482 C240.910264,193.504394 251.345455,170.835585 254.848876,146.665529 C255.874878,139.634696 256,137.557972 256,128.025062 C256,118.492153 255.874878,116.415429 254.848876,109.384595 C247.892082,61.3197135 213.683675,20.9362052 167.288368,5.97379012 C159.105376,3.32158945 150.396872,1.49507389 140.637341,0.394160408 C138.234995,0.143952798 121.693842,-0.131275573 119.616813,0.0688905149 L119.616813,0.0688905149 Z M172.017986,77.4831252 C173.219159,78.0836234 174.195112,79.2345784 174.545455,80.435575 C174.74565,81.0861148 174.795699,94.9976579 174.74565,126.348671 L174.670577,171.336 L166.73783,159.17591 L158.780059,147.01582 L158.780059,114.313685 C158.780059,93.1711423 158.880156,81.2862808 159.030303,80.7108033 C159.430694,79.3096407 160.306549,78.2087272 161.507722,77.5581875 C162.533724,77.0327515 162.909091,76.98271 166.837928,76.98271 C170.541544,76.98271 171.19218,77.0327515 172.017986,77.4831252 Z" />
  </svg>
);

export default function CopyCodeModal({ isOpen, setIsOpen, agId }: CopyCodeModalProps) {
    const { captureEvent } = useAnalytics();

    const handleCopy = async (platform: string) => {
        let textToCopy = "";
        if (platform === "static") {
            textToCopy = getStaticEmbedCode(agId);
        } else if (platform === "nextjs") {
            textToCopy = getNextEmbedCode(agId);
        }
        try {
            await navigator.clipboard.writeText(textToCopy);
            toast.success('Code copied to clipboard', toastConfig);
            captureEvent('copy_embed_code', {
                agent_id: agId,
                platform: platform
            });
        } catch (err) {
            toast.error('Failed to copy code', toastConfig);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Code2 className="w-5 h-5" />
                        Embed Your Agent
                    </DialogTitle>
                    <DialogDescription>
                        Choose your platform and copy the embed code to integrate your agent.
                    </DialogDescription>
                </DialogHeader>

                <CopyCodeComponent agId={agId} onCopy={handleCopy} />

                <div className="mt-6">
                    <Button
                        variant="outline"
                        className="flex items-center gap-2"
                        onClick={() => window.open("https://docs.aimdoc.ai/", "_blank")}
                    >
                        <ExternalLink className="w-4 h-4" />
                        View Integration Docs
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export type { CopyCodeModalProps, CopyCodeComponentProps }; 